import { QuestionContent } from "../tests/TestQuestionContent";

const OPTION_MARKS = ["А", "Б", "В", "Г", "Д", "Е", "Ж", "З"];

/** One-question form step for an ordinary, uninterrupted test flow. */
export default function ProfTestQuestionStep({
    question,
    index,
    total,
    selectedOptionIndex,
    selectedOptionIndexes = [],
    customAnswer = "",
    textValue,
    onSelectOption,
    onTextChange,
    onCustomAnswerChange,
    onPrevious,
    onNext,
    isLast,
}) {
    const options = Array.isArray(question?.options) ? question.options : [];
    const hasOptions = options.length > 0;
    const isMultiple = question?.answer_type === "multiple";
    const isRequired = question?.required !== false;
    const allowsCustomAnswer = hasOptions && Boolean(question?.allow_custom_answer);
    const hasCustomAnswer = Boolean(String(customAnswer || "").trim());
    const hasAnswer = hasOptions
        ? (isMultiple ? selectedOptionIndexes.length > 0 || hasCustomAnswer : selectedOptionIndex != null || hasCustomAnswer)
        : Boolean(String(textValue || "").trim());
    const canProceed = !isRequired || hasAnswer;
    const progress = ((index + 1) / Math.max(total, 1)) * 100;

    return (
        <main className="px-4 py-6 md:py-10">
            <div
                data-testid="prof-question-form"
                className="mx-auto w-full max-w-3xl rounded-[28px] border border-violet-200/80 bg-violet-50/45 shadow-[0_24px_80px_rgba(76,29,149,0.10)]"
            >
                <section className="overflow-hidden rounded-[27px]">
                    <div className="h-2 bg-gradient-to-r from-violet-700 via-indigo-600 to-amber-400" />

                    <header className="border-b border-slate-200/70 bg-white px-5 py-5 md:px-8 md:py-6">
                        <div className="flex items-start justify-between gap-5">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-violet-700">
                                    Профессиональный тест
                                </p>
                                <p className="mt-2 text-sm text-slate-500">
                                    {hasOptions
                                        ? `${isMultiple ? "Выберите один или несколько ответов" : "Выберите один ответ"} и переходите к следующему вопросу.`
                                        : "Введите ответ и переходите к следующему вопросу."}
                                </p>
                            </div>
                            <span className="shrink-0 rounded-full bg-violet-50 px-3 py-1.5 text-xs font-semibold tabular-nums text-violet-800 ring-1 ring-violet-100">
                                {index + 1} из {total}
                            </span>
                        </div>
                        <div className="mt-5 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-label={`Прогресс ${Math.round(progress)}%`}>
                            <div
                                className="h-full rounded-full bg-violet-600 transition-all duration-300"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    </header>

                    <div className="bg-[#f8f7fc] p-4 md:p-7">
                        <article className="rounded-2xl border border-slate-200/80 bg-white px-5 py-6 shadow-sm md:px-8 md:py-8">
                            <div className="flex items-start gap-3">
                                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-violet-500" aria-hidden="true" />
                                <div
                                    className="min-w-0 flex-1 text-base font-normal leading-7 text-slate-900 md:text-[17px]"
                                    data-testid={`prof-question-${question.id}`}
                                >
                                    <QuestionContent
                                        value={question.text}
                                        className="[&_b]:font-semibold [&_strong]:font-semibold [&_li]:my-1 [&_ol]:my-3 [&_ol]:pl-6 [&_p]:my-3 [&_table]:text-sm [&_ul]:my-3 [&_ul]:pl-6 md:[&_table]:text-base"
                                    />
                                </div>
                            </div>
                            {question.explanation ? (
                                <p className="ml-5 mt-4 whitespace-pre-wrap text-sm leading-6 text-slate-600">
                                    {question.explanation}
                                </p>
                            ) : null}
                            <div
                                data-testid="prof-question-requirement"
                                className={`ml-5 mt-4 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ring-1 ${
                                    isRequired
                                        ? "bg-rose-50 text-rose-700 ring-rose-100"
                                        : "bg-slate-50 text-slate-600 ring-slate-200"
                                }`}
                            >
                                <span
                                    className={`h-1.5 w-1.5 rounded-full ${isRequired ? "bg-rose-500" : "bg-slate-400"}`}
                                    aria-hidden="true"
                                />
                                {isRequired ? "Обязательный вопрос" : "Можно пропустить"}
                            </div>

                            <div className="mt-7">
                                {hasOptions ? (
                                    <div className="space-y-3" role={isMultiple ? "group" : "radiogroup"} aria-label="Варианты ответа">
                                        {options.map((option, optionIndex) => {
                                            const selected = isMultiple
                                                ? selectedOptionIndexes.includes(optionIndex)
                                                : selectedOptionIndex === optionIndex;
                                            return (
                                                <button
                                                    key={`${question.id}-${optionIndex}`}
                                                    type="button"
                                                    role={isMultiple ? "checkbox" : "radio"}
                                                    aria-checked={selected}
                                                    onClick={() => onSelectOption(optionIndex, option)}
                                                    data-testid={`prof-option-${optionIndex}`}
                                                    className={`group flex w-full items-start gap-3.5 rounded-xl border px-4 py-3.5 text-left transition-all md:px-5 md:py-4 ${
                                                        selected
                                                            ? "border-violet-500 bg-violet-50/80 ring-2 ring-violet-200/70"
                                                            : "border-slate-200 bg-white hover:border-violet-300 hover:bg-violet-50/35"
                                                    }`}
                                                >
                                                    <span
                                                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold transition-colors ${
                                                            selected
                                                                ? "border-violet-600 bg-violet-600 text-white"
                                                                : "border-slate-300 bg-white text-slate-500 group-hover:border-violet-400"
                                                        }`}
                                                        aria-hidden="true"
                                                    >
                                                        {selected ? "✓" : OPTION_MARKS[optionIndex] || optionIndex + 1}
                                                    </span>
                                                    <span className="pt-0.5 text-[15px] leading-relaxed text-slate-800 md:text-base">
                                                        {option}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                        {allowsCustomAnswer && (
                                            <label className={`block rounded-xl border px-4 py-3.5 transition-all md:px-5 md:py-4 ${hasCustomAnswer ? "border-violet-500 bg-violet-50/80 ring-2 ring-violet-200/70" : "border-slate-200 bg-white"}`}>
                                                <span className="mb-2 block text-[15px] font-medium text-slate-800 md:text-base">
                                                    {question.custom_answer_label || "Другой вариант"}
                                                </span>
                                                <input
                                                    type="text"
                                                    value={customAnswer}
                                                    onChange={(event) => onCustomAnswerChange(event.target.value)}
                                                    placeholder="Введите свой ответ"
                                                    data-testid="prof-custom-answer"
                                                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-violet-500 focus:ring-4 focus:ring-violet-100"
                                                />
                                            </label>
                                        )}
                                    </div>
                                ) : (
                                    <label className="block">
                                        <span className="sr-only">Ваш ответ</span>
                                        {question?.answer_type === "short_text" ? (
                                            <input
                                                type="text"
                                                className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3.5 text-base text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100"
                                                value={textValue || ""}
                                                onChange={(event) => onTextChange(event.target.value)}
                                                placeholder="Введите короткий ответ"
                                                data-testid="prof-free-text"
                                            />
                                        ) : (
                                            <textarea
                                                className="min-h-[180px] w-full resize-y rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-3.5 text-base leading-relaxed text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-100 disabled:bg-slate-100"
                                                value={textValue || ""}
                                                onChange={(event) => onTextChange(event.target.value)}
                                                placeholder="Введите развернутый ответ"
                                                data-testid="prof-free-text"
                                            />
                                        )}
                                    </label>
                                )}
                            </div>
                        </article>

                        <footer className="mt-4 flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
                            <p className="text-xs leading-relaxed text-slate-500">
                                Ответьте на вопрос, затем нажмите «Далее».
                            </p>
                            <div className="flex gap-2">
                            {index > 0 && (
                                <button type="button" onClick={onPrevious} className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 hover:border-violet-300 hover:bg-violet-50">
                                    Назад
                                </button>
                            )}
                            <button
                                type="button"
                                disabled={!canProceed}
                                onClick={onNext}
                                data-testid="prof-next"
                                className="shrink-0 rounded-xl bg-violet-700 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-800 focus:outline-none focus:ring-4 focus:ring-violet-200 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {isLast ? "Отправить ответы" : "Далее"}
                            </button>
                            </div>
                        </footer>
                    </div>
                </section>
            </div>
        </main>
    );
}
